import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Briefcase, Save, CheckCircle2 } from "lucide-react";
import { Button } from "./ui/button";
import RoleSelect from "./RoleSelect";
import { useAuth, API } from "../App";
import { toast } from "sonner";

const RoleSettingsCard = () => {
  const { getAuthHeaders } = useAuth();
  const [roleInfo, setRoleInfo] = useState({
    role_category: "",
    role_title: "",
    role_other: "",
  });
  const [initial, setInitial] = useState(null);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/auth/me`, {
        headers: getAuthHeaders(),
        withCredentials: true,
      });
      const next = {
        role_category: res.data?.role_category || "",
        role_title: res.data?.role_title || "",
        role_other: res.data?.role_other || "",
      };
      setRoleInfo(next);
      setInitial(next);
    } catch {
      // not authed — card just renders empty
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders]);

  useEffect(() => {
    load();
  }, [load]);

  const dirty =
    initial &&
    (initial.role_title !== roleInfo.role_title ||
      initial.role_other !== roleInfo.role_other);

  const handleSave = async () => {
    if (!roleInfo.role_title) {
      toast.error("Please pick a role");
      return;
    }
    if (roleInfo.role_title === "Other" && !roleInfo.role_other.trim()) {
      toast.error("Please specify your role");
      return;
    }
    setSaving(true);
    try {
      await axios.put(
        `${API}/auth/role`,
        roleInfo,
        { headers: getAuthHeaders(), withCredentials: true }
      );
      toast.success("Role updated");
      setInitial(roleInfo);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save role");
    } finally {
      setSaving(false);
    }
  };

  if (loading) return null;

  return (
    <div
      className="bg-white rounded-xl border border-slate-200 p-6"
      data-testid="role-settings-card"
    >
      <div className="flex items-start gap-4 mb-4">
        <div className="w-12 h-12 bg-primary/10 rounded-xl flex items-center justify-center flex-shrink-0">
          <Briefcase className="w-6 h-6 text-primary" />
        </div>
        <div className="flex-1">
          <h3 className="font-semibold text-slate-900 mb-1 flex items-center gap-2">
            Your role
            {initial?.role_title && (
              <CheckCircle2 className="w-4 h-4 text-emerald-500" aria-label="Set" />
            )}
          </h3>
          <p className="text-slate-600 text-sm">
            This helps others find you in the directory and personalises your
            experience.
          </p>
        </div>
      </div>
      <RoleSelect
        value={roleInfo}
        onChange={setRoleInfo}
        labelText="My role in healthcare"
        testIdPrefix="profile-role"
      />
      <div className="flex justify-end mt-4">
        <Button
          onClick={handleSave}
          disabled={saving || !dirty}
          className="bg-primary text-white hover:bg-primary/90 rounded-full"
          data-testid="save-role-btn"
        >
          {saving ? (
            "Saving…"
          ) : (
            <span className="flex items-center gap-2">
              <Save className="w-4 h-4" /> Save role
            </span>
          )}
        </Button>
      </div>
    </div>
  );
};

export default RoleSettingsCard;
